import React, { useEffect, useState, useCallback, useRef } from 'react';
import { WithTranslationsProps } from 'react-utilities';
import { Loading } from 'react-style-guide';
import { GameGrid } from '../../common/components/GameGrid';
import { TBuildEventProperties } from '../../common/components/GameTileUtils';
import bedev2Services from '../../common/services/bedev2Services';
import { TGameData } from '../../common/types/bedev1Types';
import { TOmniRecommendationGameSort } from '../../common/types/bedev2Types';
import { pageContext } from '../../common/constants/eventStreamConstants';
import {
  newGrid,
  useGameImpressionGridTracker,
  TBuildGridGameImpressionsEventProperties
} from '../../common/hooks/useGameImpressionGridTracker';
import { common } from '../../common/constants/configConstants';

type TSortDetailGridV2DiscoveryApiProps = {
  sort: TOmniRecommendationGameSort;
  homePageSessionInfo: string | undefined;
  translate: WithTranslationsProps['translate'];
};

export const SortDetailGridV2DiscoveryApi = ({
  sort,
  translate,
  homePageSessionInfo
}: TSortDetailGridV2DiscoveryApiProps): JSX.Element => {
  const gridRef = useRef<HTMLDivElement>(null);
  const tileRef = useRef<HTMLDivElement>(null);
  const [gameData, setGameData] = useState<TGameData[] | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const hasMoreRows = useCallback(() => (gameData || []).length < sort.recommendationList.length, [
    gameData,
    sort
  ]);

  const loadData = useCallback(async () => {
    setLoading(true);
    const numberOfLoadedGameData = (gameData || []).length;
    const recommendationsToLoad = sort.recommendationList.slice(
      numberOfLoadedGameData,
      numberOfLoadedGameData + common.numberOfGameTilesPerLoad
    );
    try {
      const { contentMetadata } = await bedev2Services.getOmniRecommendationsMetadata(
        recommendationsToLoad,
        homePageSessionInfo
      );
      setGameData([
        ...(gameData || []),
        ...recommendationsToLoad.map(data => contentMetadata[data.contentType][data.contentId])
      ]);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
    setLoading(false);
  }, [gameData, sort]);

  const onScroll = useCallback(() => {
    const gridOffset = (gridRef.current?.offsetTop || 0) + (gridRef.current?.clientHeight || 0);
    const windowOffset = window.pageYOffset + window.innerHeight;
    if (windowOffset > gridOffset && !hasMoreRows()) {
      window.removeEventListener('scroll', onScroll);
    } else if (windowOffset > gridOffset && hasMoreRows() && loading === false) {
      // eslint-disable-next-line no-void
      void loadData();
    }
  }, [hasMoreRows, loadData, loading]);

  const buildEventProperties: TBuildEventProperties = (data, id) => ({
    placeId: data.placeId,
    universeId: data.universeId,
    isAd: data.isSponsored,
    position: id,
    numberOfLoadedTiles: (gameData || []).length,
    gameSetTypeId: sort.topicId,
    homePageSessionInfo,
    page: pageContext.sortDetailPageHome
  });

  const buildGameImpressionsProperties: TBuildGridGameImpressionsEventProperties = useCallback(
    (viewedIndex: number[]) => {
      if (gameData !== undefined) {
        const parsedViewedIndex = viewedIndex.filter(id => id < gameData?.length);
        return {
          rootPlaceIds: parsedViewedIndex.map(id => gameData[id].placeId),
          universeIds: parsedViewedIndex.map(id => gameData[id].universeId),
          absPositions: parsedViewedIndex,
          adsPositions: parsedViewedIndex.map(id => (gameData[id].isSponsored ? 1 : 0)),
          homePageSessionInfo,
          emphasisFlag: false,
          page: pageContext.sortDetailPageHome
        };
      }

      return undefined;
    },
    [gameData, homePageSessionInfo]
  );

  useGameImpressionGridTracker(
    tileRef,
    false,
    buildGameImpressionsProperties,
    newGrid.calculateViewedGameData
  );

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [onScroll, sort]);

  useEffect(() => {
    setGameData(undefined);
    // eslint-disable-next-line no-void
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  return (
    <React.Fragment>
      <GameGrid
        ref={gridRef}
        emphasis={false}
        tileRef={tileRef}
        gameData={gameData || []}
        translate={translate}
        buildEventProperties={buildEventProperties}
      />
      {loading && <Loading />}
    </React.Fragment>
  );
};

export default SortDetailGridV2DiscoveryApi;
